import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ForumIcon from '@material-ui/icons/Forum';
import SendIcon from '@material-ui/icons/Send';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import { useDispatch } from 'react-redux';

import { useStyles } from './MessagingPage.styles';
import { LeftMenuDrawer } from '../../navigation/LeftMenuDrawer/LeftMenuDrawer';
import { SearchMenuBar } from '../../navigation/SearchMenuBar';
import useIsDesktop from '../../../hooks/useIsDesktop/useIsDesktop';
import {
  useDriverThreads,
  useDriverThread,
  useSendMessage,
  useMarkAsRead,
} from '../../../hooks/useMessaging';
import { useActiveDriversRequest } from '../../../hooks/useActiveDrivers/useActiveDrivers';
import { BroadcastComposeModal } from './BroadcastComposeModal';
import { setUnreadCount } from '../../../redux/ducks/MessagingDucks';
import { compareMessageTimes } from '../../../utils/MessageTime';
import { MessageTimestamp } from './MessageTimestamp';

/**
 * Determines whether a driver is currently reachable in-app.
 * Only drivers with status `busy` or `free` receive in-app messages;
 * all others (status `soon`, offline, or not in the active list) receive push.
 */
function isDriverActive(driverId, driverList) {
  if (!driverList || !driverId) return false;
  return driverList.some(
    (d) =>
      String(d.id) === String(driverId) &&
      (d.status === 'busy' || d.status === 'free')
  );
}

/**
 * Left panel: list of all driver thread summaries for today.
 * `optimisticallyReadIds` is a Set of driver IDs whose unread dot should be hidden
 * immediately while the mark-as-read PATCH is in flight (before the next poll).
 */
function DriverThreadList({ threads, isLoading, selectedDriverId, onSelect, optimisticallyReadIds }) {
  const classes = useStyles();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!threads.length) {
    return (
      <Box className={classes.emptyStateContainer}>
        <ForumIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.3 }} />
        <Typography variant="body2">No messages today</Typography>
      </Box>
    );
  }

  return (
    <>
      {threads.map((thread) => {
        const isSelected =
          String(thread.driverId) === String(selectedDriverId);
        const isOptimisticallyRead =
          optimisticallyReadIds && optimisticallyReadIds.has(String(thread.driverId));
        const showUnreadDot = thread.unreadCount > 0 && !isOptimisticallyRead;
        return (
          <Box
            key={thread.driverId}
            className={clsx(classes.threadListItem, {
              [classes.threadListItemSelected]: isSelected,
            })}
            onClick={() => onSelect(thread.driverId)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSelect(thread.driverId);
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={0.25}
            >
              <Typography className={classes.threadListItemName}>
                {thread.driverFirstName} {thread.driverLastName}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <Typography className={classes.threadListItemTime}>
                  <MessageTimestamp value={thread.lastMessageAt} />
                </Typography>
                {showUnreadDot && (
                  <Box className={classes.unreadDot} title="Unread messages" />
                )}
              </Box>
            </Box>
            <Typography className={classes.threadListItemPreview}>
              {thread.lastMessageText || '—'}
            </Typography>
          </Box>
        );
      })}
    </>
  );
}

DriverThreadList.propTypes = {
  threads: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  selectedDriverId: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
  optimisticallyReadIds: PropTypes.instanceOf(Set),
};

/**
 * Right panel: chronological thread of messages for a single driver.
 */
function MessageThread({ driverId, threads, onMarkStart, onMarkSettled }) {
  const classes = useStyles();
  const bottomRef = useRef(null);

  const { messages: rawMessages, isLoading } = useDriverThread(driverId);
  const { markAsRead } = useMarkAsRead();

  const markedRef = useRef(null);

  const messages = [...(rawMessages ?? [])].sort(
    (a, b) => compareMessageTimes(a.sentAt, b.sentAt)
  );

  const selectedThread = threads.find(
    (t) => String(t.driverId) === String(driverId)
  );
  const driverName = selectedThread
    ? `${selectedThread.driverFirstName} ${selectedThread.driverLastName}`
    : 'Driver';

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (markedRef.current !== driverId) {
      markedRef.current = null;
    }
  }, [driverId]);

  useEffect(() => {
    if (!driverId || !rawMessages || rawMessages.length === 0) return;
    if (markedRef.current === driverId) return;
    const unreadIds = rawMessages
      .filter((m) => m.senderRole === 'driver' && m.readAt == null)
      .map((m) => m.id);
    markedRef.current = driverId;
    if (unreadIds.length > 0) {
      if (onMarkStart) onMarkStart(driverId);
      markAsRead(
        { driverId, messageIds: unreadIds },
        {
          onSuccess: () => { if (onMarkSettled) onMarkSettled(driverId); },
          onError: () => { if (onMarkSettled) onMarkSettled(driverId); },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId, rawMessages]);

  if (isLoading) {
    return (
      <Box
        flex={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!messages.length) {
    return (
      <Box className={classes.emptyStateContainer}>
        <Typography variant="body2">No messages yet.</Typography>
        <Typography variant="body2" style={{ marginTop: 4 }}>
          Send one below.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.messageThreadContainer}>
      {messages.map((msg) => {
        const isDispatcher = msg.senderRole === 'dispatcher';
        return (
          <Box
            key={msg.id}
            display="flex"
            flexDirection="column"
            alignItems={isDispatcher ? 'flex-end' : 'flex-start'}
          >
            <Box
              className={
                isDispatcher
                  ? classes.bubbleDispatcher
                  : classes.bubbleDriver
              }
            >
              <Typography variant="body2">{msg.body}</Typography>
            </Box>
            <Typography
              className={clsx(classes.bubbleMeta, {
                [classes.bubbleMetaRight]: isDispatcher,
              })}
            >
              {isDispatcher ? 'You' : driverName} &middot;{' '}
              <MessageTimestamp value={msg.sentAt} />
              {msg.deliveryMethod === 'push' && ' · via push'}
            </Typography>
          </Box>
        );
      })}
      <div ref={bottomRef} />
    </Box>
  );
}

MessageThread.propTypes = {
  driverId: PropTypes.any.isRequired,
  threads: PropTypes.array.isRequired,
  onMarkStart: PropTypes.func,
  onMarkSettled: PropTypes.func,
};

/**
 * Compose box with text input and Send button.
 * Shows a "(via push)" hint when the driver is offline.
 */
function ComposeBox({ driverId, driverList }) {
  const classes = useStyles();
  const [text, setText] = useState('');
  const { send, isSending } = useSendMessage();
  const active = isDriverActive(driverId, driverList);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || !driverId) return;
    await send({ driverId, text: trimmed, type: 'direct' });
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box>
      {!active && (
        <Typography className={classes.pushLabel}>
          This driver is offline — message will be sent via push notification.
        </Typography>
      )}
      <Box className={classes.composeBox}>
        <TextField
          className={classes.composeTextField}
          variant="outlined"
          size="small"
          multiline
          rowsMax={4}
          placeholder="Type a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSending}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={isSending || !text.trim()}
          endIcon={isSending ? <CircularProgress size={14} /> : <SendIcon />}
          style={{ minWidth: 80, alignSelf: 'flex-end' }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
}

ComposeBox.propTypes = {
  driverId: PropTypes.any.isRequired,
  driverList: PropTypes.array,
};

/**
 * The full MessagingPage with split-panel layout.
 * Desktop: left thread list + right message thread side-by-side.
 * Mobile: list view first; tapping a driver shows the thread (back button returns to list).
 */
export function MessagingPage() {
  const classes = useStyles();
  const isDesktop = useIsDesktop();
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [pendingMarkReadIds, setPendingMarkReadIds] = useState(new Set());
  const dispatch = useDispatch();

  const { threads, isLoading: threadsLoading, refetch: refetchThreads } = useDriverThreads();
  const { driverList, refetch: refetchDrivers } = useActiveDriversRequest();

  useEffect(() => {
    refetchDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const total = threads.reduce((sum, thread) => {
      if (pendingMarkReadIds.has(String(thread.driverId))) return sum;
      return sum + (thread.unreadCount ?? 0);
    }, 0);
    dispatch(setUnreadCount(total));
  }, [threads, pendingMarkReadIds, dispatch]);

  const handleMarkStart = (driverId) => {
    setPendingMarkReadIds((prev) => {
      const next = new Set(prev);
      next.add(String(driverId));
      return next;
    });
  };

  const handleMarkSettled = (driverId) => {
    setPendingMarkReadIds((prev) => {
      const next = new Set(prev);
      next.delete(String(driverId));
      return next;
    });
  };

  const showThread = selectedDriverId != null;

  const handleBroadcastSent = () => {
    refetchThreads();
  };

  const broadcastButton = (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      startIcon={<AnnouncementIcon />}
      onClick={() => setBroadcastOpen(true)}
      style={{ whiteSpace: 'nowrap', marginLeft: 8 }}
    >
      Broadcast
    </Button>
  );

  if (isDesktop) {
    return (
      <main className={classes.content} aria-label="messaging page">
        <LeftMenuDrawer />
        <Box className={classes.header} display="flex" alignItems="center">
          <Box flex={1}>
            <SearchMenuBar title="Messaging" />
          </Box>
          <Box pr={1}>
            {broadcastButton}
          </Box>
        </Box>
        <Box className={classes.body}>
          <Box className={classes.desktopPanelLeft}>
            <Typography className={classes.panelTitle}>
              Conversations
            </Typography>
            <Box className={classes.threadListContainer}>
              <DriverThreadList
                threads={threads}
                isLoading={threadsLoading}
                selectedDriverId={selectedDriverId}
                onSelect={setSelectedDriverId}
                optimisticallyReadIds={pendingMarkReadIds}
              />
            </Box>
          </Box>

          <Box className={classes.desktopPanelRight}>
            {showThread ? (
              <>
                <MessageThread
                  driverId={selectedDriverId}
                  threads={threads}
                  onMarkStart={handleMarkStart}
                  onMarkSettled={handleMarkSettled}
                />
                <ComposeBox
                  driverId={selectedDriverId}
                  driverList={driverList}
                />
              </>
            ) : (
              <Box className={classes.emptyStateContainer}>
                <ForumIcon
                  style={{ fontSize: 64, marginBottom: 12, opacity: 0.25 }}
                />
                <Typography variant="body1" color="textSecondary">
                  Select a conversation to start messaging
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <BroadcastComposeModal
          open={broadcastOpen}
          onClose={() => setBroadcastOpen(false)}
          onSent={handleBroadcastSent}
        />
      </main>
    );
  }

  return (
    <main className={classes.content} aria-label="messaging page">
      <LeftMenuDrawer />
      <Box className={classes.header}>
        {showThread ? (
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={() => setSelectedDriverId(null)}
              aria-label="back to conversations"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" style={{ marginLeft: 8 }}>
              {(() => {
                const t = threads.find(
                  (x) => String(x.driverId) === String(selectedDriverId)
                );
                return t
                  ? `${t.driverFirstName} ${t.driverLastName}`
                  : 'Conversation';
              })()}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" alignItems="center">
            <Box flex={1}>
              <SearchMenuBar title="Messaging" />
            </Box>
            {broadcastButton}
          </Box>
        )}
      </Box>

      <Box className={classes.body}>
        {showThread ? (
          <Box className={classes.mobilePanelFull}>
            <MessageThread
              driverId={selectedDriverId}
              threads={threads}
              onMarkStart={handleMarkStart}
              onMarkSettled={handleMarkSettled}
            />
            <ComposeBox
              driverId={selectedDriverId}
              driverList={driverList}
            />
          </Box>
        ) : (
          <Box className={classes.threadListContainer}>
            <DriverThreadList
              threads={threads}
              isLoading={threadsLoading}
              selectedDriverId={selectedDriverId}
              onSelect={setSelectedDriverId}
              optimisticallyReadIds={pendingMarkReadIds}
            />
          </Box>
        )}
      </Box>

      <BroadcastComposeModal
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        onSent={handleBroadcastSent}
      />
    </main>
  );
}
